import isEqual from "lodash/isEqual";
import * as React from "react";

export type DataTableSortDirection = "asc" | "desc";

export interface DataTableSort {
  columnId: string;
  direction: DataTableSortDirection;
}

/**
 * Imperative actions exposed by a mounted `<DataTable>`. The hook holds the
 * handle ref slot; `<DataTable>` populates it via `useImperativeHandle`.
 * Consumers usually don't reach for the handle directly — the methods on
 * the `Table<TData>` instance (e.g. `setColumnVisibility`, `clearSelection`,
 * `refreshCells`) call through to it on their behalf.
 */
export interface DataTableHandle {
  /** Clear all selections. */
  deselectAll(): void;
  /**
   * Show or hide a single column by colId. Caller passes the desired
   * next state; no read-back required. Internal columns (selection,
   * expand) are filtered by the picker UI, not enforced here.
   */
  setColumnVisibility(id: string, visible: boolean): void;
  /**
   * Force AG Grid to re-invoke cell renderers for the given column ids
   * (or every visible column when omitted). Useful when a cell renderer
   * closes over external data — like a memoized lookup map — that
   * resolves asynchronously: the row data hasn't changed, so AG Grid
   * wouldn't repaint on its own, and stale values would linger until
   * the next row-data refresh.
   */
  refreshCells(columnIds?: string[]): void;
}

export type DataTableLoadingState = "foreground" | "background" | "idle";

/**
 * Translate a TanStack Query result (or compatible boolean pair) into the
 * `<DataTable>` `loading` prop. `isLoading` (initial fetch, no cached data)
 * yields `"foreground"` so the skeleton overlay shows; `isFetching` alone
 * (background refetch with cached rows on screen) yields `"background"`;
 * otherwise `"idle"`. The structural parameter lets multi-query callers OR
 * their booleans together at the call site.
 */
export function deriveLoadingState(query: {
  isLoading: boolean;
  isFetching: boolean;
}): DataTableLoadingState {
  if (query.isLoading) {
    return "foreground";
  }
  if (query.isFetching) {
    return "background";
  }
  return "idle";
}

/**
 * Canonical page-size choices surfaced by the DataTable footer. Single
 * source of truth across all DataTable consumers and any saved-preferences
 * UI that surfaces a "rows per page" control. Don't fork this list
 * per-domain.
 */
export const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

/**
 * Saved user preferences seeded into the table at mount. The wire shape
 * (`TableUIPreferences`) uses snake_case; this is the camelCase view
 * consumers receive from the per-domain preferences hooks (e.g.
 * `useDatasetTablePreferences`).
 */
export interface DataTablePreferences {
  columns?: string[];
  rowsPerPage?: number;
}

/**
 * TanStack-style universal updater: setters accept either a value or a
 * function `(prev) => next`. Removes the stale-closure footgun for
 * consumers deriving the next value from the current one.
 */
export type Updater<T> = T | ((old: T) => T);

/**
 * Resolve an `Updater<T>` against the previous value. Use inside `setState`
 * callbacks when the resolved value is needed for a side effect (e.g.
 * emitting a preference change).
 */
export function functionalUpdate<T>(updater: Updater<T>, old: T): T {
  return typeof updater === "function"
    ? (updater as (o: T) => T)(old)
    : updater;
}

/**
 * Options for `useTable`. The hook owns truly table-only state (sort,
 * pagination, selection, column visibility). Filter state lives at the
 * `useResourceView` layer.
 */
export interface UseTableOptions<TData> {
  /**
   * Pure row → id projection. Required so the cross-page selection
   * accumulator and AG Grid share a single identity strategy. Captured
   * once at mount; later changes are ignored.
   */
  getRowId: (row: TData) => string;
  /**
   * Initial sort applied on first render. Captured once at mount; later
   * changes are ignored — the hook is the source of truth from then on.
   */
  initialSort?: DataTableSort;
  /**
   * Saved user preferences (page size, column order). Captured once at
   * mount; later changes are ignored. Consumers should fetch preferences
   * before rendering the table (e.g. via a parent suspense boundary).
   */
  preferences?: DataTablePreferences;
  /**
   * Fired when the user changes a persisted preference. Receives the full
   * merged preferences object — the hook tracks the latest emitted shape
   * so editing one dimension (e.g. page size) doesn't clobber a prior
   * edit to another (e.g. column order). The consumer is responsible for
   * persisting.
   */
  onPreferencesChange?: (next: DataTablePreferences) => void;
  /**
   * Default ordered list of visible column ids, used when no
   * `preferences.columns` are present at mount. Pass `undefined` to
   * defer entirely to ColDef-level `hide` defaults — the hook treats
   * an absent option differently from an explicit empty array (which
   * means "hide everything by default").
   */
  defaultVisibleColumnIds?: string[];
}

/**
 * Table instance returned by `useTable`. State-readers are exposed as
 * properties; mutators are methods. Pass the whole instance to
 * `<DataTable table={table} ... />`.
 *
 * Whole-state setters accept an `Updater<T>` so consumers can derive
 * the next value from `prev` without reading the current value off
 * the closure.
 */
export interface Table<TData> {
  // ── State readers ──
  readonly sort: DataTableSort | null;
  readonly pageSize: number;
  readonly currentPage: number;
  readonly canGetPrevPage: boolean;
  /** Cross-page selection accumulator — full `TData` rows, not just ids.
   *  Captured via the row→id projection in `UseTableOptions.getRowId`. */
  readonly selection: TData[];
  readonly visibleColumnIds: Set<string>;

  // ── Mutators ──
  // Declared as function properties (not method shorthand) so that consumers
  // can detach them and pass them as props without ESLint's
  // `@typescript-eslint/unbound-method` rule complaining about `this`
  // scoping — these are plain `useCallback` closures over `useState`
  // setters, not class methods, so detachment is always safe.
  readonly setSort: (updater: Updater<DataTableSort | null>) => void;
  readonly setPageSize: (updater: Updater<number>) => void;
  readonly setColumnOrder: (updater: Updater<string[]>) => void;
  /** Granular setter — column-id + boolean, no Updater shape. Imperative:
   *  flows through the AG Grid handle, which fires `onColumnVisible`,
   *  which `<DataTable>` mirrors back into React state. */
  readonly setColumnVisibility: (id: string, visible: boolean) => void;

  // ── Action-style methods ──
  readonly nextPage: () => void;
  readonly prevPage: () => void;
  readonly resetCurrentPage: () => void;
  /** Clears the cross-page selection accumulator and calls
   *  `gridApi.deselectAll()` on the mounted table. */
  readonly clearSelection: () => void;
  /** Imperative cell-refresh — used when a cell renderer closes over
   *  external data (e.g. an async lookup map) that resolves without the
   *  row data changing. AG Grid wouldn't repaint on its own. */
  readonly refreshCells: (columnIds?: string[]) => void;

  // ── Internal surface ──
  //
  // These fields exist on the public `Table<TData>` interface only because
  // `<DataTable>` reads them and TypeScript doesn't have a way to give one
  // consumer privileged access. They are NOT intended for consumer code to
  // read. The naming (`initial*`, `*Ref`, grid-mirror `on*Change`) telegraphs
  // the intent — read once at mount, ref slot, grid → state mirror — but
  // the type doesn't stop a consumer from reading them. Enforcement is by
  // convention and code review.
  /** Mount-snapshot sort, passed to AG Grid `initialState.sort`. */
  readonly initialSort: DataTableSort | null;
  /** Mount-snapshot visible columns. `null` means "no preferences/defaults
   *  provided" and the grid should fall through to ColDef `hide` defaults. */
  readonly initialVisibleColumnIds: string[] | null;
  /** Imperative handle slot wired up by `<DataTable>` via `useImperativeHandle`. */
  readonly handleRef: React.RefObject<DataTableHandle | null>;
  /** Row → id projection captured at mount; identity strategy used by both
   *  the cross-page selection accumulator and AG Grid's per-row identity. */
  readonly getRowId: (row: TData) => string;
  /** Read by `<DataTable>` to re-hydrate AG Grid's per-node selection
   *  when rows shift (paginating back to a page with prior selections). */
  readonly selectionMap: ReadonlyMap<string, TData>;
  /** Grid → state mirror: fired from AG Grid `onColumnVisible`. */
  readonly onVisibleColumnChange: (id: string, visible: boolean) => void;
  /** Grid → state mirror: fired from AG Grid `onRowSelected`. */
  readonly onRowSelectionChange: (
    row: TData,
    id: string,
    isSelected: boolean,
  ) => void;
  /** Grid → state mirror: fired from AG Grid `onSelectionChanged` (bulk). */
  readonly onBulkSelectionChange: (
    selectedRows: TData[],
    affectedIds: Set<string>,
  ) => void;
}

/**
 * Hook that owns the table-only state for a `<DataTable>` instance. Pass the
 * returned `Table<TData>` to `<DataTable table={...} ... />`. For filter
 * state (and a compound shell), use `useResourceView` from
 * `@/shared/components/ResourceView` — it composes this hook and layers
 * filter state on top.
 */
export function useTable<TData>(opts: UseTableOptions<TData>): Table<TData> {
  const {
    getRowId,
    initialSort: initialSortOption,
    preferences,
    onPreferencesChange,
    defaultVisibleColumnIds,
  } = opts;

  // ── Mount snapshots ─────────────────────────────────────────────────
  // getRowId is a ref so the cross-page selection accumulator uses one
  // identity strategy for the table's lifetime. Same lock-in
  // legacy `useDataTableState` applied historically before the M3 refactor.
  const stableGetRowIdRef = React.useRef(getRowId);
  const [snapshotPreferences] = React.useState<DataTablePreferences>(
    () => preferences ?? {},
  );
  const [initialSort] = React.useState<DataTableSort | null>(
    () => initialSortOption ?? null,
  );
  // `null` means "neither prefs nor defaults were provided" — the grid
  // should skip `initialState.columnVisibility` seeding and let ColDef
  // `hide` defaults take effect. An empty array is preserved as a real
  // "explicitly nothing visible" intent.
  const [initialVisibleColumnIds] = React.useState<string[] | null>(
    () => snapshotPreferences.columns ?? defaultVisibleColumnIds ?? null,
  );

  // ── Live state ──────────────────────────────────────────────────────
  const [sort, setSortInternal] = React.useState<DataTableSort | null>(
    initialSort,
  );
  const [pageSize, setPageSizeInternal] = React.useState(
    snapshotPreferences.rowsPerPage ?? 50,
  );
  const [currentPage, setCurrentPage] = React.useState(0);
  // Single shared empty-map identity used for every "clear selection" path
  // (`clearSelection`, `resetCurrentPage`). Keeps identity stable across
  // clears so `React.memo`'d row renderers can bail out cheaply. Per-mount
  // (not module-level) so the generic `TData` is captured cleanly.
  const emptySelectionMap = React.useMemo(() => new Map<string, TData>(), []);
  // Replaced on every mutation (never mutated in place) so React's
  // Object.is re-render check fires.
  const [selectionMap, setSelectionMap] =
    React.useState<Map<string, TData>>(emptySelectionMap);
  const [visibleColumnList, setVisibleColumnList] = React.useState<string[]>(
    () => initialVisibleColumnIds ?? [],
  );

  // ── Refs (persist without re-render) ────────────────────────────────
  const tableRef = React.useRef<DataTableHandle | null>(null);
  const latestPreferencesRef =
    React.useRef<DataTablePreferences>(snapshotPreferences);

  // ── Derived (memoized) ──────────────────────────────────────────────
  const visibleColumnIds = React.useMemo(
    () => new Set(visibleColumnList),
    [visibleColumnList],
  );
  const selection = React.useMemo(
    () => Array.from(selectionMap.values()),
    [selectionMap],
  );

  // ── Internal helpers ────────────────────────────────────────────────
  const emitPreferences = React.useCallback(
    (next: DataTablePreferences) => {
      latestPreferencesRef.current = next;
      onPreferencesChange?.(next);
    },
    [onPreferencesChange],
  );

  const resetCurrentPage = React.useCallback(() => {
    setCurrentPage(0);
    // Selection is also cleared on any "underlying query changed" transition
    // (sort/pageSize/filter) — same behavior as the legacy hook.
    setSelectionMap(emptySelectionMap);
  }, [emptySelectionMap]);

  // ── Mutators ────────────────────────────────────────────────────────
  const setSort = React.useCallback(
    (updater: Updater<DataTableSort | null>) => {
      setSortInternal(updater);
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const setPageSize = React.useCallback(
    (updater: Updater<number>) => {
      // Side effect (emitPreferences) needs the resolved value, so resolve
      // inside the setter. StrictMode dev runs the updater twice; the
      // resulting `emitPreferences` payload is idempotent so the double-fire
      // is harmless.
      setPageSizeInternal((prev) => {
        const next = functionalUpdate(updater, prev);
        emitPreferences({
          ...latestPreferencesRef.current,
          rowsPerPage: next,
        });
        return next;
      });
      resetCurrentPage();
    },
    [emitPreferences, resetCurrentPage],
  );

  // AG Grid owns column visibility. Method → handle → gridApi → AG Grid
  // fires `onColumnVisible` → <DataTable> calls back into
  // `onVisibleColumnChange` to mirror the change into React state.
  const setColumnVisibility = React.useCallback(
    (id: string, visible: boolean) => {
      tableRef.current?.setColumnVisibility(id, visible);
    },
    [],
  );

  const onVisibleColumnChange = React.useCallback(
    (id: string, visible: boolean) => {
      setVisibleColumnList((prev) => {
        if (prev.includes(id) === visible) {
          return prev;
        }
        const next = visible
          ? [...prev, id]
          : prev.filter((columnId) => columnId !== id);
        emitPreferences({
          ...latestPreferencesRef.current,
          columns: next,
        });
        return next;
      });
    },
    [emitPreferences],
  );

  const setColumnOrder = React.useCallback(
    (updater: Updater<string[]>) => {
      setVisibleColumnList((prev) => {
        const next = functionalUpdate(updater, prev);
        if (isEqual(prev, next)) {
          return prev;
        }
        emitPreferences({
          ...latestPreferencesRef.current,
          columns: next,
        });
        return next;
      });
    },
    [emitPreferences],
  );

  const onRowSelectionChange = React.useCallback(
    (row: TData, id: string, isSelected: boolean) => {
      setSelectionMap((prev) => {
        const next = new Map(prev);
        if (isSelected) {
          next.set(id, row);
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    [],
  );

  const onBulkSelectionChange = React.useCallback(
    (selectedRows: TData[], affectedIds: Set<string>) => {
      const projectRowId = stableGetRowIdRef.current;
      setSelectionMap((prev) => {
        const next = new Map(prev);
        const selectedIdsInScope = new Set<string>();
        for (const row of selectedRows) {
          const id = projectRowId(row);
          selectedIdsInScope.add(id);
          next.set(id, row);
        }
        // For ids the bulk op touched but didn't select, drop from accumulator.
        for (const id of affectedIds) {
          if (!selectedIdsInScope.has(id)) {
            next.delete(id);
          }
        }
        return next;
      });
    },
    [],
  );

  const nextPage = React.useCallback(
    () => setCurrentPage((prev) => prev + 1),
    [],
  );
  const prevPage = React.useCallback(
    () => setCurrentPage((prev) => Math.max(0, prev - 1)),
    [],
  );

  const clearSelection = React.useCallback(() => {
    setSelectionMap(emptySelectionMap);
    tableRef.current?.deselectAll();
  }, [emptySelectionMap]);

  const refreshCells = React.useCallback((columnIds?: string[]) => {
    tableRef.current?.refreshCells(columnIds);
  }, []);

  const canGetPrevPage = currentPage > 0;

  // ── Instance ────────────────────────────────────────────────────────
  return React.useMemo<Table<TData>>(
    () => ({
      // readers
      sort,
      pageSize,
      currentPage,
      canGetPrevPage,
      selection,
      visibleColumnIds,
      // mutators
      setSort,
      setPageSize,
      setColumnVisibility,
      setColumnOrder,
      // actions
      nextPage,
      prevPage,
      resetCurrentPage,
      clearSelection,
      refreshCells,
      // internal surface
      initialSort,
      initialVisibleColumnIds,
      handleRef: tableRef,
      getRowId: stableGetRowIdRef.current,
      selectionMap,
      onVisibleColumnChange,
      onRowSelectionChange,
      onBulkSelectionChange,
    }),
    [
      sort,
      pageSize,
      currentPage,
      canGetPrevPage,
      selection,
      visibleColumnIds,
      setSort,
      setPageSize,
      setColumnVisibility,
      setColumnOrder,
      nextPage,
      prevPage,
      resetCurrentPage,
      clearSelection,
      refreshCells,
      initialSort,
      initialVisibleColumnIds,
      selectionMap,
      onVisibleColumnChange,
      onRowSelectionChange,
      onBulkSelectionChange,
    ],
  );
}
